// HTML and URL sanitizing. Everything that enters the canvas goes through sanitizeFragment():
// imported HTML, pasted HTML, code edited by hand and the HTML block.

const DROPPED_ELEMENTS = new Set([
    'script', 'style', 'link', 'meta', 'base', 'object', 'embed', 'applet', 'frame', 'frameset',
    'noscript', 'template', 'portal', 'foreignobject', 'animate', 'set', 'animatemotion', 'animatetransform',
]);

const URL_ATTRIBUTES = new Set(['href', 'src', 'action', 'formaction', 'poster', 'cite', 'background', 'data', 'xlink:href', 'longdesc', 'manifest']);
const DROPPED_ATTRIBUTES = new Set(['srcdoc', 'formaction', 'ping']);

const SAFE_SCHEMES = /^(?:https?|mailto|tel|sms|callto|geo):/i;
const SAFE_DATA_IMAGE = /^data:image\/(?:png|jpe?g|gif|webp|avif|bmp|svg\+xml)(?:;[a-z0-9=._+-]+)*(?:;base64)?,/i;

/**
 * Returns the URL when it is safe for the given use, an empty string otherwise.
 * Relative URLs, anchors, http(s), mailto and tel are allowed; data: only for images.
 */
export function safeUrl(value, { image = false } = {}) {
    const url = String(value ?? '').trim();
    if (url === '') return '';
    // Browsers ignore control characters and whitespace inside the scheme ("java\tscript:").
    const compact = url.replace(/[\u0000- \u007f-\u009f]+/g, '');
    const scheme = compact.match(/^([a-z][a-z0-9+.-]*):/i);
    if (!scheme) return url; // relative URL, anchor, query or protocol-relative
    if (SAFE_SCHEMES.test(compact)) return url;
    if (image && SAFE_DATA_IMAGE.test(compact)) return url;
    return '';
}

function safeSrcset(value) {
    return String(value || '')
        .split(',')
        .map(part => part.trim())
        .filter(part => {
            const url = part.split(/\s+/)[0];
            return url && safeUrl(url, { image: true }) !== '';
        })
        .join(', ');
}

function safeStyle(value) {
    const style = String(value || '');
    return /expression\s*\(|javascript:|vbscript:|-moz-binding|behavior\s*:/i.test(style.replace(/\\/g, '')) ? '' : style;
}

function cleanElement(el) {
    for (const attr of Array.from(el.attributes)) {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on') || DROPPED_ATTRIBUTES.has(name)) {
            el.removeAttribute(attr.name);
        } else if (URL_ATTRIBUTES.has(name)) {
            if (attr.value.trim() === '') continue; // an empty address is harmless (an image waiting for its file)
            const image = name === 'src' || name === 'poster' || name === 'background' || name === 'data';
            const clean = safeUrl(attr.value, { image: image && el.localName !== 'iframe' });
            if (clean === '') el.removeAttribute(attr.name);
        } else if (name === 'srcset') {
            el.setAttribute(attr.name, safeSrcset(attr.value));
        } else if (name === 'style') {
            const clean = safeStyle(attr.value);
            if (clean === '') el.removeAttribute('style');
        }
    }
    // An iframe may only show a web page: no sandbox escape through javascript:, data: or srcdoc.
    if (el.localName === 'iframe') {
        const src = el.getAttribute('src') || '';
        if (src && !/^(?:https?:)?\/\//i.test(src.trim()) && !src.trim().startsWith('/')) el.removeAttribute('src');
    }
    if (el.localName === 'a' && el.getAttribute('target') === '_blank') {
        const rel = new Set(String(el.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
        rel.add('noopener');
        el.setAttribute('rel', [...rel].join(' '));
    }
}

/** Sanitizes a node tree in place (a fragment or an element). */
export function sanitizeTree(root) {
    const walker = (root.ownerDocument || root).createTreeWalker(root, 1 /* SHOW_ELEMENT */);
    const dropped = [];
    let node = walker.nextNode();
    while (node) {
        if (DROPPED_ELEMENTS.has(node.localName)) dropped.push(node);
        else cleanElement(node);
        node = walker.nextNode();
    }
    dropped.forEach(el => el.remove());
    // Comments may hide conditional markup; they never belong to page content.
    const comments = (root.ownerDocument || root).createTreeWalker(root, 128 /* SHOW_COMMENT */);
    const remove = [];
    while (comments.nextNode()) remove.push(comments.currentNode);
    remove.forEach(comment => comment.remove());
    return root;
}

/**
 * Parses HTML into an inert fragment of `doc` and sanitizes it.
 * A <template> never runs scripts nor loads images while it is parsed.
 */
export function parseFragment(html, doc = document, sanitize = true) {
    const template = doc.createElement('template');
    template.innerHTML = String(html ?? '');
    const fragment = template.content;
    if (typeof sanitize === 'function') {
        const clean = sanitize(String(html ?? ''));
        template.innerHTML = String(clean ?? '');
        return sanitizeTree(template.content); // a custom sanitizer never lets scripts into the canvas
    }
    return sanitizeTree(fragment);
}

export function sanitizeHtml(html, doc = document) {
    const template = doc.createElement('template');
    template.content.append(parseFragment(html, doc));
    return template.innerHTML;
}
